import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { leiderschapApi } from '../api';
import useLeadershipMembers from '../hooks/useLeadershipMembers';
import type { AgendaItem } from '../types';
import AgendaItemSheet from './AgendaItemSheet';

export default function AgendaTab({ meetingId, canManage }: { meetingId: string; canManage: boolean }) {
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { user } = useAuth();
  const members = useLeadershipMembers(meetingId);

  const fetchItems = async () => {
    try {
      const data = await leiderschapApi.listAgendaItems(meetingId);
      setItems(data);
    } catch (error) {
      toast.error('Kon agenda niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await leiderschapApi.createAgendaItem(meetingId, { title: newTitle.trim() });
      setNewTitle('');
      fetchItems();
    } catch (error) {
      toast.error('Agendapunt toevoegen mislukt');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      await leiderschapApi.deleteAgendaItem(meetingId, itemId);
      fetchItems();
    } catch (error: any) {
      // e.g. 409: the agenda point still has linked action items.
      toast.error(error?.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const selectedItem = items.find((i) => i.id === selectedId) || null;

  return (
    <div className="space-y-3">
      {!loading && items.length === 0 && <p className="text-sm text-slate-500">Nog geen agendapunten.</p>}
      {items.map((item, index) => (
        // The whole row opens the detail sheet; the drag handle and delete
        // button stop propagation so they stay independent.
        <div
          key={item.id}
          data-testid="agenda-row"
          onClick={() => setSelectedId(item.id)}
          className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3 transition-colors hover:bg-slate-50 focus-within:ring-2 focus-within:ring-teal-500"
        >
          <span
            data-testid="agenda-drag-handle"
            className="mt-0.5 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4 text-slate-300" aria-hidden />
          </span>
          <button type="button" className="min-w-0 flex-1 text-left focus:outline-none">
            <span className="block break-words text-sm font-medium text-slate-900">{index + 1}. {item.title}</span>
            {item.description && <span className="mt-1 block break-words text-sm text-slate-500">{item.description}</span>}
          </button>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" aria-hidden />
          {canManage && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDelete(item.id); }}
              aria-label="Agendapunt verwijderen"
            >
              <Trash2 className="h-4 w-4 text-slate-400" />
            </Button>
          )}
        </div>
      ))}

      {canManage && (
        <form onSubmit={handleAdd} className="flex gap-2 pt-2">
          <Input
            value={newTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTitle(e.target.value)}
            placeholder="Nieuw agendapunt..."
            className="flex-1"
          />
          <Button type="submit" disabled={adding || !newTitle.trim()}>
            <Plus className="mr-2 h-4 w-4" />
            Toevoegen
          </Button>
        </form>
      )}

      {selectedItem && (
        <AgendaItemSheet
          key={selectedItem.id}
          meetingId={meetingId}
          item={selectedItem}
          position={items.findIndex((i) => i.id === selectedItem.id) + 1}
          canManage={canManage}
          currentUserId={user?.id}
          members={members}
          onClose={() => setSelectedId(null)}
          onItemSaved={(saved) => setItems((current) => current.map((i) => (i.id === saved.id ? saved : i)))}
        />
      )}
    </div>
  );
}
